import * as React from "react";
import { useState } from "react";
import { useLoadPersonalization, useSavePersonalization } from "~/Redux/Personalization";
import { addPortfolio } from "~/Redux/portfolios";
import { commonClientResx } from "~/resxDist/commonClientResx";
import { commonResx } from "~/resxDist/commonResx";
import { portfoliosResx } from "~/resxDist/portfoliosResx";
import { ButtonCancel, ButtonPositive } from "../buttons";
import { Switcher } from "../buttons/Switcher";
import { useMounted } from "../hooks/useMounted";
import { Indent } from "../indent";
import { Input } from "../inputs";
import { ModalPopup } from "../modalPopup/modalPopup";
import * as styles from "./CreatePortfolioModal.less";
import { PortfolioList } from "./PortfolioList";

export interface CreatePortfolioModalProps {
    excludedPortfolioId: number;
    inputMaxLength: number;
    name: string;
    callback: F1<number>;
    createPortfolio: typeof addPortfolio;
    closeModal(): void;
}

export const CreatePortfolioModal = (props: CreatePortfolioModalProps) => {
    return (
        <ModalPopup
            title={portfoliosResx.add_portfolio}
            width={500}
            close={props.closeModal}
            cross={true}
            data-testid="create-portfolio-modal"
        >
            {/*Content was placed to separate component CreatePortfolioInner because onBlurAction caused the popup rerender,
               and because of this, focus moved to DefaultFocusPoint
             */}
            <CreatePortfolioInner {...props} />
        </ModalPopup>
    );
};

const valuesKey = "copy_custom_fields_from_parent_portfolio";

const CreatePortfolioInner = ({
    excludedPortfolioId,
    inputMaxLength,
    name,
    callback,
    createPortfolio,
    closeModal,
}: CreatePortfolioModalProps) => {
    const [nameValue, setNameValue] = useState(name);
    const [showCopyCustomFieldsSwitcher, setShowCopyCustomFieldsSwitcher] = useState(false);
    const [parentId, setParentId] = useState<number>();
    const [copyCustomFields, setCopyCustomFields] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showValidation, setShowValidation] = useState(false);
    const isMounted = useMounted();

    const portfolioName = nameValue.trim();

    const [personalizationInitValues, initValuesLoaded] = useLoadPersonalization(valuesKey);
    const saveValues = useSavePersonalization(valuesKey);

    const callbackAfterCreation = (id: number) => {
        callback(id);

        // prevent closing if parent component was already destroyed and modal was closed
        if (isMounted.current) {
            closeModal();
        }
    };

    const setParentIdAction = (id: number) => {
        setParentId(id);
        setShowCopyCustomFieldsSwitcher(id != null);
    };

    React.useEffect(() => {
        setCopyCustomFields(!!personalizationInitValues);
    }, [initValuesLoaded]);

    const onSave = () => {
        if (!portfolioName) {
            setNameValue("");
            return;
        }

        setIsLoading(true);
        saveValues(copyCustomFields);
        createPortfolio({
            name: portfolioName,
            parentId,
            copyCustomFields,
            callback: callbackAfterCreation,
        });
    };

    return (
        <>
            <div className={styles.modal_content} onClick={e => e.stopPropagation()}>
                <div className={styles.modal_row}>
                    <label>
                        <span className={styles.label}>{commonResx.name_title}</span>
                        <Input
                            autoFocus={true}
                            value={nameValue}
                            onChangeAction={setNameValue as any}
                            onBlurAction={() => setShowValidation(true)}
                            showValidation={showValidation && !portfolioName}
                            maxLength={inputMaxLength}
                        />
                    </label>
                </div>
                <div className={styles.modal_row}>
                    <label>
                        <span className={styles.label}>{portfoliosResx.ParentPortfolio}</span>
                        <PortfolioList
                            canCreate={false}
                            excludedPortfolioId={excludedPortfolioId}
                            onChangeAction={setParentIdAction}
                            selectTriggerPlaceholder={commonClientResx.None_Text}
                            theme={null}
                            value={parentId}
                        />
                    </label>
                </div>
                <div className={styles.modal_row}>
                    <div className={styles.switcher_wrapper}>
                        {showCopyCustomFieldsSwitcher && (
                            <Switcher
                                isOn={copyCustomFields}
                                label={portfoliosResx.copy_custom_fields_label}
                                labelAfterSwitcher={true}
                                onChangeAction={setCopyCustomFields}
                            />
                        )}
                    </div>
                </div>
            </div>
            <div>
                <ButtonPositive
                    text={portfoliosResx.add_portfolio}
                    action={onSave}
                    isLoading={isLoading}
                    disabled={!nameValue}
                />
                <Indent />
                <ButtonCancel action={closeModal} />
            </div>
        </>
    );
};
